import { useState } from "react";
import type { C1_Floor } from "../model/C1_Floor";
import c1_state from "../model/C1_State";
import { useModel } from "../model/C1_useModel";
import type { ResizePreview } from "../types/C1_AppTypes";
import { isSpecialExteriorRoomType, hasContinuousExteriorWalls } from "../types/C1_RoomTypes";
import { C1_Room } from "../model/C1_Room";
import { resizeRoomEdge } from "../model/C1_resizeRoomEdge";
import { C1_EventKey } from "../C1_EventKey";
import { useDelayedRoomControls } from "./useDelayedRoomControls";

type DimensionAxis = "width" | "height";
type DimensionEdge = "start" | "end";

export function C1_RoomDimensions({ floor, resizePreview }: { floor: C1_Floor; resizePreview?: ResizePreview | null }) {
  useModel(c1_state);
  const [editing, setEditing] = useState<DimensionAxis | null>(null);
  const [editingEdge, setEditingEdge] = useState<DimensionEdge>("end");
  const [hovered, setHovered] = useState<{ axis: DimensionAxis; edge: DimensionEdge } | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [inputChanged, setInputChanged] = useState(false);
  const selection = c1_state.selection;
  const justPlacedObject = c1_state.justPlacedObject.value;
  const room =
    selection instanceof C1_Room ? selection : justPlacedObject instanceof C1_Room ? justPlacedObject : undefined;
  const justPlaced = room !== undefined && room === justPlacedObject;
  const showRoomControls = useDelayedRoomControls(room);

  if (
    !room ||
    !showRoomControls ||
    (room.firstSelection && !justPlaced) ||
    c1_state.floorImageRenderOptions.value ||
    c1_state.editMode.value !== "building"
  )
    return null;

  const project = c1_state.project;
  const roomRect = room.rect(project);
  // Solid exterior walls and parapets reduce the usable dimensions by their wall thickness.
  const dashedSpecialRoom = isSpecialExteriorRoomType(room.type) && !hasContinuousExteriorWalls(room.type);
  const currentInterior = dashedSpecialRoom ? roomRect : room.innerRect();
  const displayedRoomRect =
    resizePreview?.room === room && !resizePreview.extrudesWall ? resizePreview.rect : roomRect;
  const interior = {
    x1: displayedRoomRect.x1 + (currentInterior.x1 - roomRect.x1),
    x2: displayedRoomRect.x2 - (roomRect.x2 - currentInterior.x2),
    y1: displayedRoomRect.y1 + (currentInterior.y1 - roomRect.y1),
    y2: displayedRoomRect.y2 - (roomRect.y2 - currentInterior.y2),
  };
  const PX = c1_state.svgPx;
  const view = c1_state.view;
  const x = (interior.x1 - view.x1) * PX;
  const y = (interior.y1 - view.y1) * PX;
  const width = (interior.x2 - interior.x1) * PX;
  const height = (interior.y2 - interior.y1) * PX;
  const widthM = interior.x2 - interior.x1;
  const heightM = interior.y2 - interior.y1;
  const offset = 38;
  const tick = 6;
  const topWallThickness = (currentInterior.y1 - roomRect.y1) * 2 * PX;
  const leftWallThickness = (currentInterior.x1 - roomRect.x1) * 2 * PX;
  const horizontalY = y - topWallThickness - offset;
  const verticalX = x - leftWallThickness - offset;
  const locale = state.settings.uiLanguage;
  const displayDimension = (value: number) => Math.round((value + 1e-9) * 100) / 100;
  const editDimension = (value: number) => Math.round((value + 1e-9) * 1000) / 1000;
  const formatEditDimension = (value: number) => editDimension(value).toFixed(3).replace(/0$/, "");
  const format = (value: number) =>
    new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      displayDimension(value),
    );

  const startEditing = (axis: DimensionAxis, edge: DimensionEdge, value: number) => {
    setEditing(axis);
    setEditingEdge(edge);
    setHovered({ axis, edge });
    setInputValue(formatEditDimension(value));
    setInputChanged(false);
  };

  const applyDimension = (axis: DimensionAxis, rawValue: string) => {
    const value = Number.parseFloat(rawValue.replace(",", "."));
    if (Number.isFinite(value) && value >= 0.1) {
      const roundedValue = Math.round(value * 1000) / 1000;
      const wallAllowance = axis === "width" ? roomRect.x2 - roomRect.x1 - widthM : roomRect.y2 - roomRect.y1 - heightM;
      const nextAxisSize = roundedValue + wallAllowance;
      const nextRect = { ...roomRect };
      if (axis === "width") {
        if (editingEdge === "start") nextRect.x1 = roomRect.x2 - nextAxisSize;
        else nextRect.x2 = roomRect.x1 + nextAxisSize;
      } else if (editingEdge === "start") nextRect.y1 = roomRect.y2 - nextAxisSize;
      else nextRect.y2 = roomRect.y1 + nextAxisSize;
      const side =
        axis === "width" ? (editingEdge === "start" ? "left" : "right") : editingEdge === "start" ? "top" : "bottom";
      const vertical = side === "left" || side === "right";
      resizeRoomEdge({
        floor,
        project,
        room,
        side,
        segmentStart: vertical ? roomRect.y1 : roomRect.x1,
        segmentEnd: vertical ? roomRect.y2 : roomRect.x2,
        nextRect,
        snappingDisabled: true,
        preserveExactSize: true,
      });
    }
  };

  const commit = (axis: DimensionAxis) => {
    if (inputChanged) {
      applyDimension(axis, inputValue);
      c1_state.eventLogger.addEvent(C1_EventKey.RoomDimensionChanged, {
        roomId: room.id,
        roomName: (room.owner?.name ?? ""),
        dimension: axis,
        value: Number.parseFloat(inputValue.replace(",", ".")),
      });
      state.undoManager.userInteractionEnded();
    }
    setEditing(null);
    setHovered(null);
    setInputChanged(false);
  };

  const label = (axis: DimensionAxis, value: number, labelX: number, labelY: number, rotation = 0) => {
    const transform = rotation ? `rotate(${rotation} ${labelX} ${labelY})` : undefined;
    const visibleEdge = editing === axis ? editingEdge : hovered?.axis === axis ? hovered.edge : null;
    const startArrowX = axis === "height" ? labelX + 40 : labelX - 40;
    const endArrowX = axis === "height" ? labelX - 40 : labelX + 40;
    if (editing === axis) {
      return (
        <>
          <g transform={transform}>
            <text
              className="C1_roomDimensionArrow"
              x={startArrowX}
              y={labelY}
              dominantBaseline="central"
              textAnchor="middle"
            >
              {visibleEdge === "start" ? (axis === "height" ? "▶" : "◀") : ""}
            </text>
            <text
              className="C1_roomDimensionArrow"
              x={endArrowX}
              y={labelY}
              dominantBaseline="central"
              textAnchor="middle"
            >
              {visibleEdge === "end" ? (axis === "height" ? "◀" : "▶") : ""}
            </text>
          </g>
          <foreignObject x={labelX - 38} y={labelY - 14} width={76} height={28} overflow="visible">
            <input
              // @ts-expect-error xmlns is required for HTML inside SVG foreignObject
              xmlns="http://www.w3.org/1999/xhtml"
              type="number"
              min="0.1"
              step="0.1"
              value={inputValue}
              className="C1_dimensionInput"
              autoFocus
              onChange={(event) => {
                setInputValue(event.target.value);
                setInputChanged(true);
              }}
              onBlur={() => commit(axis)}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === "Enter") event.currentTarget.blur();
                if (event.key === "Escape") {
                  setEditing(null);
                  setHovered(null);
                  setInputChanged(false);
                }
              }}
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
            />
          </foreignObject>
        </>
      );
    }

    return (
      <g
        className="C1_dimensionLabel"
        transform={transform}
        onPointerEnter={() => setHovered({ axis, edge: "end" })}
        onPointerLeave={() => setHovered(null)}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          startEditing(axis, "end", value);
        }}
      >
        <rect x={labelX - 27} y={labelY - 10} width={54} height={18} rx={5} />
        <text
          className="C1_roomDimensionArrow"
          x={startArrowX}
          y={labelY}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {visibleEdge === "start" ? (axis === "height" ? "▶" : "◀") : ""}
        </text>
        <text className="C1_roomDimensionArrow" x={endArrowX} y={labelY} textAnchor="middle" dominantBaseline="central">
          {visibleEdge === "end" ? (axis === "height" ? "◀" : "▶") : ""}
        </text>
        <text x={labelX} y={labelY} textAnchor="middle" dominantBaseline="central">
          {format(value)}
        </text>
      </g>
    );
  };

  return (
    <g className="C1_dimensions" data-c1-svg-layer="room-dimensions" onDoubleClick={(event) => event.stopPropagation()}>
      <g className="C1_dimensionGroup">
        <line
          className="C1_dimensionHitLine C1_dimensionHitLineAbove"
          transform="translate(0 -16)"
          x1={x}
          y1={horizontalY}
          x2={x + width / 2}
          y2={horizontalY}
          onPointerEnter={() => setHovered({ axis: "width", edge: "start" })}
          onPointerLeave={() => !editing && setHovered(null)}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            startEditing("width", "start", widthM);
          }}
        />
        <line
          className="C1_dimensionHitLine C1_dimensionHitLineAbove"
          transform="translate(0 -16)"
          x1={x + width / 2}
          y1={horizontalY}
          x2={x + width}
          y2={horizontalY}
          onPointerEnter={() => setHovered({ axis: "width", edge: "end" })}
          onPointerLeave={() => !editing && setHovered(null)}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            startEditing("width", "end", widthM);
          }}
        />
        <line x1={x} y1={y} x2={x} y2={horizontalY + tick} />
        <line x1={x + width} y1={y} x2={x + width} y2={horizontalY + tick} />
        <line x1={x} y1={horizontalY} x2={x + width} y2={horizontalY} />
        <line x1={x} y1={horizontalY - tick} x2={x} y2={horizontalY + tick} />
        <line x1={x + width} y1={horizontalY - tick} x2={x + width} y2={horizontalY + tick} />
        {label("width", widthM, x + width / 2, horizontalY - 8)}
      </g>
      <g className="C1_dimensionGroup">
        <line
          className="C1_dimensionHitLine"
          x1={verticalX}
          y1={y}
          x2={verticalX}
          y2={y + height / 2}
          onPointerEnter={() => setHovered({ axis: "height", edge: "start" })}
          onPointerLeave={() => !editing && setHovered(null)}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            startEditing("height", "start", heightM);
          }}
        />
        <line
          className="C1_dimensionHitLine"
          x1={verticalX}
          y1={y + height / 2}
          x2={verticalX}
          y2={y + height}
          onPointerEnter={() => setHovered({ axis: "height", edge: "end" })}
          onPointerLeave={() => !editing && setHovered(null)}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            startEditing("height", "end", heightM);
          }}
        />
        <line x1={x} y1={y} x2={verticalX + tick} y2={y} />
        <line x1={x} y1={y + height} x2={verticalX + tick} y2={y + height} />
        <line x1={verticalX} y1={y} x2={verticalX} y2={y + height} />
        <line x1={verticalX - tick} y1={y} x2={verticalX + tick} y2={y} />
        <line x1={verticalX - tick} y1={y + height} x2={verticalX + tick} y2={y + height} />
        {label("height", heightM, verticalX - 8, y + height / 2, -90)}
      </g>
    </g>
  );
}
